import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../../axiosInstance";

const columns = [
  { id: "id", label: "ID", minWidth: 100, align: "center" },
  { id: "points", label: "POINTS", minWidth: 150, align: "center" },
  {
    id: "promo_code_used_by",
    label: "PROMO CODE USED BY",
    minWidth: 150,
    align: "center",
  },
  {
    id: "promo_code",
    label: "PROMO CODE",
    minWidth: 150,
    align: "center",
  },
  {
    id: "created_at",
    label: "DATE",
    minWidth: 150,
    align: "center",
  },
];

const formatDate = (dateString) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  if (isNaN(date)) return "--";
  return date.toLocaleDateString("en-GB");
};

const safeValue = (val) =>
  val === null || val === undefined || val === "" ? "--" : val;

export function ViewReferralAgentsDetailes() {
  const [loading, setLoading] = useState(false);
  const [agentData, setAgentData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const navigate = useNavigate();
  const { id } = useParams();

  const fetchReferralAgentViewDetailes = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(`/admin/agent-profile/${id}/`);
      console.log("✅ agent profile view list response:", res.data);
      if (res.data.success) {
        setAgentData(res.data.data);
        setTransactions(res.data.data.transactions || []);
      } else {
        setError("Failed to fetch agent profile view data.");
      }
    } catch (err) {
      console.error("❌ Error fetching agent profile view data:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralAgentViewDetailes();
  }, [id]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <div>
      <div className="admin-top-container">
        <div className="admin-listText">
          <IoArrowBack
            className="back-icon"
            onClick={() => {
              navigate("/referral-agents");
            }}
          />{" "}
          {safeValue(agentData?.first_name)}
        </div>
      </div>

      <div>
        <div className="referral-agents-details">
          {/* Cards */}
          <div className="details-grid">
            {/* General Information */}
            <div className="details-card">
              <h3>General Information</h3>
              <p>
                <strong>First Name:</strong> {safeValue(agentData?.first_name)}
              </p>
              <p>
                <strong>Last Name:</strong> {safeValue(agentData?.last_name)}
              </p>
              <p>
                <strong>Email ID:</strong> {safeValue(agentData?.email)}
              </p>
              <p>
                <strong>Phone Number:</strong> {safeValue(agentData?.mobile_no)}
              </p>
            </div>

            {/* Additional Information */}
            <div className="details-card">
              <h3>Additional Information</h3>
              <p>
                <strong>User ID:</strong> {safeValue(agentData?.id)}
              </p>
              <p>
                <strong>Referral ID:</strong>{" "}
                {safeValue(agentData?.user_referal_code?.code)}
              </p>
              <p>
                <strong>User Count:</strong>{" "}
                {safeValue(agentData?.user_referal_code?.user_count)}
              </p>
              <p>
                <strong>Points:</strong>{" "}
                {safeValue(agentData?.user_referal_code?.points)}
              </p>
            </div>

            {/* Bank Information */}
            <div className="details-card">
              <h3>Bank Information</h3>
              <p>
                <strong>Bank Account No.:</strong>{" "}
                {safeValue(agentData?.bank_account_number)}
              </p>
              <p>
                <strong>Bank Name:</strong> {safeValue(agentData?.bank_name)}
              </p>
              <p>
                <strong>IFSC Code:</strong> {safeValue(agentData?.ifsc_code)}
              </p>
            </div>

            {/* Address Information */}
            <div className="details-card">
              <h3>Address Information</h3>
              <p>
                <strong>Door No:</strong> {safeValue(agentData?.door_no)}
              </p>
              <p>
                <strong>Street:</strong> {safeValue(agentData?.street)}
              </p>
              <p>
                <strong>Area:</strong> {safeValue(agentData?.area)}
              </p>
              <p>
                <strong>City:</strong> {safeValue(agentData?.city)}
              </p>
              <p>
                <strong>State:</strong> {safeValue(agentData?.state)}
              </p>
              <p>
                <strong>Pincode:</strong> {safeValue(agentData?.pincode)}
              </p>
            </div>
          </div>
        </div>

        <Paper sx={{ width: "100%", overflow: "hidden", marginTop: 3 }}>
          <TableContainer sx={{ maxHeight: 440 }} className="Table-container">
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      className="table-header"
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : transactions.length > 0 ? (
                  transactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow
                        className="table-row"
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={index}
                        sx={{
                          backgroundColor:
                            index % 2 === 1 ? "#E7F0F9" : "white",
                        }}
                      >
                        {columns.map((column) => {
                          let value = row[column.id];
                          if (column.id === "created_at") {
                            value = formatDate(row.created_at);
                          }
                          return (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              className={`${column.id}-column tableCelldata`}
                            >
                              {safeValue(value)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      style={{ padding: "40px 0" }}
                    >
                      No Transactions Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500, 1000, 2000, 5000]}
            component="div"
            count={transactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
    </div>
  );
}
